/**
 * **Intercede on non-attack damage** (`ll1-granted-reactions` S5). RAW's trigger is "they take
 * damage from a source within your line of sight", not only an attack's, so each non-attack
 * emitter (a mine, a bulldoze run-over, an authored `damage` atom, a jockey's 4 kinetic, a
 * meltdown, dangerous terrain) asks here before it mitigates: may a protector take this damage
 * instead? The gates are the attack window's ({@link protectorCandidates}), with line of sight
 * measured to the emitter's source. Accepted, the emitter routes the full pre-mitigation amount
 * to the protector, who mitigates it with their own defenses.
 *
 * Ruling 5: a source must be positioned and seen. Burn ticks and heat overflow have none and
 * never call this; GM damage names no source and passes none.
 *
 * This is an interception, not a notification (`decisions/0175`): the emitter's damage report
 * names whoever took the damage, and the bus fires once for them after it lands.
 */

import { applyResolved, type Cell, type Combatant, type EncounterCommand, type EncounterState, type ResolvedStep } from "@lancer-vtt/domain";
import { NeedsDecisionSignal, interpretEffect, SEED_OVERLAY, type ChoiceSource, type LoadedOverlay } from "./effects";
import { recordedAnswer } from "./effects/choices";
import { combatantCellLineOfSight, combatantLineOfSight } from "./effects/spatialAnswerer";
import { AUTHORED_ACCEPT_OPTION, DECLINE_OPTION, hasRedirectPendingDamage, protectorCandidates, reactionPromptId } from "./reactionRegistry";
import type { DiceSource } from "../dice";
import type { ResolutionLedger } from "./effects/resolutionLedger";

/** Where a non-attack damage instance comes from: a combatant (a mine, a mover, a carrier) or a cell (dangerous terrain). */
export type NonAttackDamageSource = { combatant: string } | { cell: Cell };

/** One reaction a non-attack interception spent, for the caller's `spend_reaction` command. */
export type NonAttackReactionSpend = { combatantId: string; name: string };

/**
 * What a `damage` atom needs to offer Intercede ({@link import("./effects").EffectContext.intercede}):
 * the answers, whether an unanswered offer halts, and the stage's ledger, which the protector's
 * `reaction_use` is raised on and every later offer in the stage reads (`reactorsUsed`). The stage
 * that owns the sink settles the ledger into its `reactionSpentBy`.
 */
export type InterceptSink = {
  answers: Record<string, string> | ChoiceSource | undefined;
  interactive: boolean | undefined;
  ledger: ResolutionLedger;
};

export type InterceptNonAttackDamageArgs = {
  state: EncounterState;
  /** The combatant about to take the damage. */
  target: Combatant;
  /** The damage's source; absent ⇒ no offer (ruling 5). */
  source: NonAttackDamageSource | undefined;
  /**
   * Names this emitter instance in the prompt id (`mine.<mineId>`, `terrain`, …), so two
   * emitters in one resolution (a move through dangerous terrain onto a mine) never share an
   * answer.
   */
  emitter: string;
  /** Pre-mitigation damage, for the prompt text. */
  amount: number;
  dtype: string;
  /** Recorded answers: an intent's `choices` map, or an effect context's {@link ChoiceSource}. */
  choices: Record<string, string> | ChoiceSource | undefined;
  interactive: boolean | undefined;
  dice: DiceSource;
  overlay?: LoadedOverlay;
  /** Protectors that already spent a reaction earlier in this resolution. */
  spentReactions?: ReadonlySet<string>;
};

/** An accepted interception: the protector now takes the damage. */
export type NonAttackIntercept = {
  protectorId: string;
  spend: NonAttackReactionSpend;
  /** Steps the clause's other atoms produced (none shipped). */
  steps: ResolvedStep[];
  warnings: string[];
};

/**
 * Offer Intercede to each eligible protector of `target`, in id order, and return the first that
 * accepts. An unanswered offer halts with a {@link NeedsDecisionSignal} routed to the protector
 * when `interactive`, and declines otherwise. Pure over `(state, choices)`. Reactions open only in
 * sequenced combat ({@link inSequencedCombat}).
 */
export function interceptNonAttackDamage(args: InterceptNonAttackDamageArgs): NonAttackIntercept | undefined {
  const { state, target, source, emitter, amount, dtype, choices, interactive, dice, spentReactions } = args;
  if (source === undefined || amount <= 0) return undefined;
  if (!inSequencedCombat(state)) return undefined;
  const overlay = args.overlay ?? SEED_OVERLAY;
  const sourceId = "combatant" in source ? source.combatant : undefined;
  const sees = (protectorId: string) => ("combatant" in source ? combatantLineOfSight(state, protectorId, source.combatant) : combatantCellLineOfSight(state, protectorId, source.cell));
  const candidates = protectorCandidates({
    state,
    targetId: target.id,
    ...(sourceId !== undefined ? { attackerId: sourceId } : {}),
    sees,
    event: { family: "damage" },
    overlay,
    ...(spentReactions ? { spentReactions } : {}),
  });
  for (const { protector, h, idx, reactionName } of candidates) {
    const { redirect, rest } = hasRedirectPendingDamage(h.effect.effect);
    if (!redirect) continue; // only a redirect is meaningful outside an attack's damage window.
    const promptId = reactionPromptId(`${h.source}#${emitter}#${idx}`, protector.id);
    const options = [
      { id: AUTHORED_ACCEPT_OPTION, label: "Use" },
      { id: DECLINE_OPTION, label: "Decline" },
    ];
    const recorded = recordedAnswer(choices, promptId, options.map((o) => o.id));
    let answer: string;
    if (recorded !== undefined && options.some((o) => o.id === recorded)) answer = recorded;
    else if (interactive === true) {
      throw new NeedsDecisionSignal(promptId, `${protector.name} may Intercede — take ${target.name}'s ${amount} ${dtype} damage instead?`, { options, who: protector.id, title: "Intercede" });
    } else answer = DECLINE_OPTION;
    if (answer !== AUTHORED_ACCEPT_OPTION) continue;
    const r = interpretEffect(rest, { state, actors: { self: protector.id, target: protector.id, ...(sourceId !== undefined ? { attacker: sourceId } : {}) }, dice, overlay });
    return { protectorId: protector.id, spend: { combatantId: protector.id, name: reactionName }, steps: r.steps, warnings: [...r.warnings, `${protector.name} intercedes, taking ${target.name}'s ${amount} ${dtype} damage.`] };
  }
  return undefined;
}

/**
 * A turn is in progress, or one has already been taken. The attack windows need only the first,
 * since attacks happen inside a turn; a meltdown and an end-of-turn dangerous-terrain check run
 * after `end_turn` has set the turn idle, and are still sequenced combat.
 */
function inSequencedCombat(state: EncounterState): boolean {
  return state.activation.turn.phase === "acting" || state.activation.turnOrdinal > 0;
}

/**
 * `id` as the steps so far leave it: a protector who already took its own share of this blast
 * takes the redirected share from its current HP, not its entry HP.
 */
export function liveCombatant(state: EncounterState, steps: readonly ResolvedStep[], id: string): Combatant {
  return applyResolved(state, steps).combatants[id]!;
}

